import { useDispatch, useSelector } from "react-redux";
import { useEffect, useMemo, useRef } from "react";
import clsx from "clsx";
import { showZoomButton } from "../../shared/utility";
import zoomService from "../../services/zoomService";
import { AR_MARKUP_VIEW, CALL_BODY_SCREEN, EXTRACT_TEXT_VIEW, MARKUP_VIEW, MEASUREMENT_VIEW } from "../../constants/Constants";
import { PublicVariables } from "../../constants/PublicVariables";
import liveCaptionService from "../../services/liveCaptionService";
import {
    completeLiveCaptionUpload,
    failedLiveCaptionUpload,
    setLocalUserLiveCaptionStart,
    setLocalUserLiveCaptionStop,
} from "../../reducers/liveCaption-reducer";
import { LogFirebase } from "../../services/firestore";
import CustomToast from "../UI/customToast";
import { setIsLocationOnVideoIsVisible } from "../../reducers/common-reducer";
import googleEvent from "../../services/googleEvent";
import { EVENT_NAME } from "../../constants/GTagConstants";
import useLanguageChange from "../../hooks/useLanguageChange";
import { useFlags } from "launchdarkly-react-client-sdk";
import { isLiveCaptionFeatureAvailable } from "../BottomMenuBar/LiveCaptionsButton";

const LiveCaptions = () => {
    const dispatch = useDispatch();
    const ldFlag = useFlags();
    const localisedValues = useLanguageChange();
    const { captionList, captionTextOn, localUserLiveCaptionStart, localUserLiveCaptionStop, liveCaptionViewOn, storeTranscriptionData } =
        useSelector((state) => state.liveCaption);
    const { markupInprogress, markupLiveStream, arMarkupLiveStream } = useSelector((state) => state.markup);
    const { extractTextInprogress } = useSelector((state) => state.extractText);
    const { pointerOn } = useSelector((state) => state.livePointer);
    const { currentCallBodyScreen } = useSelector((state) => state.commonReducer);
    const { measurementView } = useSelector((state) => state.measurement);
    const captionMsgRef = useRef(null);
    const liveCaptionBoxRef = useRef(null);

    useEffect(() => {
        if (localUserLiveCaptionStart) {
            googleEvent.logEvent(EVENT_NAME.STARTED_LIVE_CAPTION);
            LogFirebase("LiveCaption", "Local user started live caption", "true");
            showStartLiveCaptionToast();
            dispatch(setLocalUserLiveCaptionStart(false));
        }
    }, [localUserLiveCaptionStart]);

    useEffect(() => {
        const isMobile = PublicVariables.isMobile;

        if (localUserLiveCaptionStop) {
            // Log analytics
            googleEvent.logEvent(EVENT_NAME.STOPPED_LIVE_CAPTION);
            LogFirebase("LiveCaption", "Local user stopped live caption", "true");
            // Handle UI updates
            if (isMobile) {
                dispatch(setIsLocationOnVideoIsVisible(true));
            }
            CustomToast.success(localisedValues?.live_captions_stopped);
            dispatch(setLocalUserLiveCaptionStop(false));
        }
    }, [localUserLiveCaptionStop]);

    useEffect(() => {
        if (storeTranscriptionData.data) {
            // 🔹 Mark upload as complete (clears pendingLiveCaptionUploadData)
            LogFirebase("LiveCaption", "Transcription data posted successfully", "true");
            dispatch(completeLiveCaptionUpload());
        } else if (storeTranscriptionData.error) {
            LogFirebase("LiveCaption", "Failed to post transcription data", storeTranscriptionData.error || "true");
            console.error("❌ Failed to post captions:", storeTranscriptionData.error);
            dispatch(failedLiveCaptionUpload());
        }
    }, [storeTranscriptionData.data, storeTranscriptionData.error]);

    /** when live caption started show toast and hide location on video */
    const showStartLiveCaptionToast = () => {
        LogFirebase("LiveCaption", "showStartLiveCaptionToast", "true");
        const toastMessage = localisedValues?.["live_captions_started"] + " " + localisedValues?.["spoken_language_is_set_to_english"];
        CustomToast.success(toastMessage);
        dispatch(setIsLocationOnVideoIsVisible(false));
    };

    // For handling caption-msg-append-text scroll
    const handleCaptionMsgScroll = () => {
        const captionMsg = captionMsgRef.current;
        if (captionMsg) {
            const captionHeight = captionMsg.scrollHeight;
            const maxCaptionHeight = 48; // Threshold for caption-msg-append-text

            if (captionHeight > maxCaptionHeight) {
                const finalHeight = captionHeight - maxCaptionHeight;
                captionMsg.style.top = `-${finalHeight}px`;
            } else {
                captionMsg.style.top = "0px";
            }
        }
    };

    // For handling live-caption-append-message scroll (live-caption-append-message)
    const handleLiveCaptionScroll = () => {
        const liveCaptionBox = liveCaptionBoxRef.current;
        if (liveCaptionBox) {
            const captionHeight = liveCaptionBox.scrollHeight;
            const maxCaptionHeight = 88; // Threshold for live-caption-append-message

            if (captionHeight > maxCaptionHeight) {
                const finalHeight = captionHeight - maxCaptionHeight;
                liveCaptionBox.style.top = `-${finalHeight}px`;
            } else {
                liveCaptionBox.style.top = "0px";
            }
        }
    };

    useEffect(() => {
        handleCaptionMsgScroll(); // For caption-msg-append-text scroll
        handleLiveCaptionScroll(); // For live-caption-append-message scroll
    }, [captionList]);

    const notifyScreen = currentCallBodyScreen === CALL_BODY_SCREEN.NOTIFY_SCREEN;
    const isMarkupInProgress = markupInprogress !== MARKUP_VIEW.OFF_VIEW;
    const isArMarkupInProgress = arMarkupLiveStream !== AR_MARKUP_VIEW.OFF_VIEW;
    const isMeasurementInProgress = measurementView !== MEASUREMENT_VIEW.OFF_VIEW;
    const isTextExtractionInProgress = extractTextInprogress !== EXTRACT_TEXT_VIEW.OFF_VIEW;
    const isFileUploadingScreen = currentCallBodyScreen === CALL_BODY_SCREEN.UPLOAD_FILE_SCREEN;

    const isHideLiveCaption =
        notifyScreen ||
        isMarkupInProgress ||
        isArMarkupInProgress ||
        isMeasurementInProgress ||
        isTextExtractionInProgress ||
        markupLiveStream ||
        pointerOn ||
        isFileUploadingScreen;

    const renderCaptionList = useMemo(() => {
        return (
            isLiveCaptionFeatureAvailable(ldFlag) &&
            liveCaptionViewOn &&
            captionTextOn &&
            captionList?.length > 0 && (
                <div className='live-caption-in'>
                    <div className='live-caption-scroll live-caption-append default-custom-scroll'>
                        <div className='live-caption-append-message' ref={liveCaptionBoxRef}>
                            {captionList.map((caption) => (
                                <div
                                    key={caption.participantID + caption.systemTimeStamp + caption.callDuration}
                                    className={`live-caption-msg cls-${caption.participantID}`}
                                >
                                    <div className='caption-name'>
                                        {liveCaptionService.allLiveCaptionUserNameWithFormat(caption.isAgent, caption.name, caption.participantID)} :{" "}
                                    </div>
                                    <div className='caption-msg'>
                                        <div className='caption-msg-scroll caption-msg-scbar caption-msg-append default-custom-scroll'>
                                            <div className='caption-msg-append-text' ref={captionMsgRef}>
                                                {caption.text}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )
        );
    }, [liveCaptionViewOn, captionTextOn, captionList]);

    return (
        <div
            className={clsx(
                "live-caption",
                !isHideLiveCaption && "active",
                !(showZoomButton() && !zoomService.checkZoomButtonAvailable()) && "no-zoom-level"
            )}
            id='live-caption'
        >
            {renderCaptionList}
        </div>
    );
};

export default LiveCaptions;
